import { create } from 'zustand';
import { toast } from 'sonner';
import {
    collaborationService,
    ReviewRequest,
    Comment,
    CollaborationStats,
    ReviewRequestFilters,
} from '../services/collaboration.service';

interface CollaborationState {
    // Data
    reviews: ReviewRequest[];
    selectedReview: ReviewRequest | null;
    comments: Comment[];
    stats: CollaborationStats | null;

    // UI State
    isLoading: boolean;
    activeTab: 'feedback' | 'pending' | 'in_review' | 'changes' | 'approvals';
    filters: ReviewRequestFilters;
    searchTerm: string;

    // Actions - Reviews
    fetchReviews: (filters?: ReviewRequestFilters) => Promise<void>;
    fetchMyReviews: () => Promise<void>;
    fetchPendingApprovals: () => Promise<void>;
    fetchReview: (id: string) => Promise<void>;
    createReview: (data: Parameters<typeof collaborationService.createReview>[0]) => Promise<ReviewRequest>;
    updateReview: (id: string, data: Partial<ReviewRequest>) => Promise<void>;
    deleteReview: (id: string) => Promise<void>;
    makeDecision: (id: string, decision: { action: 'approved' | 'rejected' | 'changes_requested'; comment?: string }) => Promise<void>;
    assignReviewer: (id: string, assignedTo: string, reviewers?: string[]) => Promise<void>;
    setSelectedReview: (review: ReviewRequest | null) => void;

    // Actions - Comments
    fetchComments: (reviewRequestId: string) => Promise<void>;
    createComment: (reviewRequestId: string, content: string, parentId?: string, mentions?: string[]) => Promise<Comment>;
    updateComment: (id: string, data: { content?: string; resolved?: boolean }) => Promise<void>;
    deleteComment: (id: string) => Promise<void>;
    resolveComment: (id: string) => Promise<void>;
    addReaction: (id: string, type: 'like' | 'dislike') => Promise<void>;

    // Actions - Stats
    fetchStats: () => Promise<void>;

    // Actions - Integration
    sendForReview: (data: Parameters<typeof collaborationService.sendForReview>[0]) => Promise<ReviewRequest>;

    // Actions - UI
    setActiveTab: (tab: 'feedback' | 'pending' | 'in_review' | 'changes' | 'approvals') => void;
    setFilters: (filters: Partial<ReviewRequestFilters>) => void;
    setSearchTerm: (term: string) => void;

    // Helpers
    getFilteredReviews: () => ReviewRequest[];
}

export const useCollaborationStore = create<CollaborationState>((set, get) => ({
    // Initial State
    reviews: [],
    selectedReview: null,
    comments: [],
    stats: null,
    isLoading: false,
    activeTab: 'feedback',
    filters: {},
    searchTerm: '',

    // Reviews Actions
    fetchReviews: async (filters?: ReviewRequestFilters) => {
        try {
            set({ isLoading: true });
            const reviews = await collaborationService.getReviews(filters || get().filters);
            set({ reviews });
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
            toast.error('Failed to load reviews');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchMyReviews: async () => {
        try {
            set({ isLoading: true });
            const reviews = await collaborationService.getMyReviews();
            set({ reviews });
        } catch (error) {
            console.error('Failed to fetch my reviews:', error);
            toast.error('Failed to load your reviews');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchPendingApprovals: async () => {
        try {
            set({ isLoading: true });
            const reviews = await collaborationService.getPendingApprovals();
            set({ reviews });
        } catch (error) {
            console.error('Failed to fetch pending approvals:', error);
            toast.error('Failed to load pending approvals');
        } finally {
            set({ isLoading: false });
        }
    },

    fetchReview: async (id: string) => {
        try {
            const review = await collaborationService.getReview(id);
            set({ selectedReview: review });
        } catch (error) {
            console.error('Failed to fetch review:', error);
            toast.error('Failed to load review details');
        }
    },

    createReview: async (data) => {
        try {
            const review = await collaborationService.createReview(data);
            set((state) => ({ reviews: [review, ...state.reviews] }));
            toast.success('Review request created');
            get().fetchStats();
            return review;
        } catch (error) {
            console.error('Failed to create review:', error);
            toast.error('Failed to create review request');
            throw error;
        }
    },

    updateReview: async (id: string, data) => {
        try {
            const updatedReview = await collaborationService.updateReview(id, data);
            set((state) => ({
                reviews: state.reviews.map((r) => (r.id === id ? updatedReview : r)),
                selectedReview: state.selectedReview?.id === id ? updatedReview : state.selectedReview,
            }));
            toast.success('Review updated');
        } catch (error) {
            console.error('Failed to update review:', error);
            toast.error('Failed to update review');
            throw error;
        }
    },

    deleteReview: async (id: string) => {
        try {
            await collaborationService.deleteReview(id);
            set((state) => ({
                reviews: state.reviews.filter((r) => r.id !== id),
                selectedReview: state.selectedReview?.id === id ? null : state.selectedReview,
            }));
            toast.success('Review deleted');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to delete review:', error);
            toast.error('Failed to delete review');
        }
    },

    makeDecision: async (id: string, decision) => {
        try {
            const updatedReview = await collaborationService.makeDecision(id, decision);
            set((state) => ({
                reviews: state.reviews.map((r) => (r.id === id ? updatedReview : r)),
                selectedReview: state.selectedReview?.id === id ? updatedReview : state.selectedReview,
            }));

            const actionMessages = {
                approved: 'Review approved!',
                rejected: 'Review rejected',
                changes_requested: 'Changes requested',
            };
            toast.success(actionMessages[decision.action]);
            get().fetchStats();
        } catch (error) {
            console.error('Failed to make decision:', error);
            toast.error('Failed to submit decision');
            throw error;
        }
    },

    assignReviewer: async (id: string, assignedTo: string, reviewers?: string[]) => {
        try {
            const data: { assignedTo: string; reviewers?: string[] } = { assignedTo };
            if (reviewers) {
                data.reviewers = reviewers;
            }
            const updatedReview = await collaborationService.assignReviewer(id, data);
            set((state) => ({
                reviews: state.reviews.map((r) => (r.id === id ? updatedReview : r)),
                selectedReview: state.selectedReview?.id === id ? updatedReview : state.selectedReview,
            }));
            toast.success('Reviewer assigned');
        } catch (error) {
            console.error('Failed to assign reviewer:', error);
            toast.error('Failed to assign reviewer');
            throw error;
        }
    },

    setSelectedReview: (review) => set({ selectedReview: review }),

    // Comments Actions
    fetchComments: async (reviewRequestId: string) => {
        try {
            const comments = await collaborationService.getComments(reviewRequestId);
            set({ comments });
        } catch (error) {
            console.error('Failed to fetch comments:', error);
            toast.error('Failed to load comments');
        }
    },

    createComment: async (reviewRequestId: string, content: string, parentId?: string, mentions?: string[]) => {
        try {
            const data: { content: string; parentId?: string; mentions?: string[] } = { content };
            if (parentId) {
                data.parentId = parentId;
            }
            if (mentions) {
                data.mentions = mentions;
            }
            const comment = await collaborationService.createComment(reviewRequestId, data);
            // Refresh comments to get proper threading
            await get().fetchComments(reviewRequestId);
            toast.success('Comment added');
            return comment;
        } catch (error) {
            console.error('Failed to create comment:', error);
            toast.error('Failed to add comment');
            throw error;
        }
    },

    updateComment: async (id: string, data) => {
        try {
            await collaborationService.updateComment(id, data);
            // Refresh comments if we have a selected review
            const { selectedReview } = get();
            if (selectedReview) {
                await get().fetchComments(selectedReview.id);
            }
            toast.success('Comment updated');
        } catch (error) {
            console.error('Failed to update comment:', error);
            toast.error('Failed to update comment');
            throw error;
        }
    },

    deleteComment: async (id: string) => {
        try {
            await collaborationService.deleteComment(id);
            // Refresh comments
            const { selectedReview } = get();
            if (selectedReview) {
                await get().fetchComments(selectedReview.id);
            }
            toast.success('Comment deleted');
        } catch (error) {
            console.error('Failed to delete comment:', error);
            toast.error('Failed to delete comment');
        }
    },

    resolveComment: async (id: string) => {
        try {
            await collaborationService.resolveComment(id);
            // Refresh comments
            const { selectedReview } = get();
            if (selectedReview) {
                await get().fetchComments(selectedReview.id);
            }
        } catch (error) {
            console.error('Failed to resolve comment:', error);
            toast.error('Failed to update comment');
        }
    },

    addReaction: async (id: string, type: 'like' | 'dislike') => {
        try {
            await collaborationService.addReaction(id, type);
            // Refresh comments
            const { selectedReview } = get();
            if (selectedReview) {
                await get().fetchComments(selectedReview.id);
            }
        } catch (error) {
            console.error('Failed to add reaction:', error);
        }
    },

    // Stats Actions
    fetchStats: async () => {
        try {
            const stats = await collaborationService.getStats();
            set({ stats });
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    },

    // Integration Actions
    sendForReview: async (data) => {
        try {
            const review = await collaborationService.sendForReview(data);
            set((state) => ({ reviews: [review, ...state.reviews] }));
            toast.success('Sent for review');
            get().fetchStats();
            return review;
        } catch (error) {
            console.error('Failed to send for review:', error);
            toast.error('Failed to send for review');
            throw error;
        }
    },

    // UI Actions
    setActiveTab: (tab) => set({ activeTab: tab }),

    setFilters: (filters) => set((state) => ({
        filters: { ...state.filters, ...filters },
    })),

    setSearchTerm: (term) => set({ searchTerm: term }),

    // Helpers
    getFilteredReviews: () => {
        const { reviews, searchTerm, filters, activeTab } = get();

        return reviews.filter((review) => {
            // Tab filter - now supports granular status tabs
            if (activeTab === 'feedback') {
                // "Active" - shows all non-completed
                if (!['pending', 'in_review', 'changes_requested'].includes(review.status)) {
                    return false;
                }
            } else if (activeTab === 'pending') {
                if (review.status !== 'pending') {
                    return false;
                }
            } else if (activeTab === 'in_review') {
                if (review.status !== 'in_review') {
                    return false;
                }
            } else if (activeTab === 'changes') {
                if (review.status !== 'changes_requested') {
                    return false;
                }
            } else if (activeTab === 'approvals') {
                if (!['approved', 'rejected'].includes(review.status)) {
                    return false;
                }
            }

            // Search filter
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                if (
                    !review.title.toLowerCase().includes(search) &&
                    !review.description?.toLowerCase().includes(search)
                ) {
                    return false;
                }
            }

            // Status filter
            if (filters.status && review.status !== filters.status) {
                return false;
            }

            // Priority filter
            if (filters.priority && review.priority !== filters.priority) {
                return false;
            }

            return true;
        });
    },
}));
